var _jsxFileName = "/codebuild/output/src2937227346/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/consents/HipaaConsent/HipaaUS.tsx";
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IconBack, InformationalScreen, LoadingModal, Stack } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import axios from 'axios';
import { useApiError, useAuth } from 'Hooks';
import { Button, Footer, MainContainer, OptInOut, RichText } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage, hasPendingConsent } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const mapStateToProps = ({
  env: {
    osType,
    osVersion,
    app,
    appVersion,
    phoneLanguage
  },
  auth,
  nav: {
    query
  },
  config: {
    documentUrls,
    documentPaths
  }
}) => {
  var _documentPaths$cms;
  return {
    osType,
    osVersion,
    app,
    appVersion,
    auth,
    isAccountProfile: !!query.accountProfile,
    cmsDomain: documentUrls.cms,
    hipaaCmsPaths: (_documentPaths$cms = documentPaths.cms) === null || _documentPaths$cms === void 0 ? void 0 : _documentPaths$cms.hipaalibre,
    phoneLanguage
  };
};
const connector = connect(mapStateToProps);
const HipaaConsent = ({
  osType,
  osVersion,
  isAccountProfile,
  app,
  appVersion,
  auth,
  cmsDomain,
  hipaaCmsPaths,
  phoneLanguage
}) => {
  var _auth$include$patient2, _auth$include$patient3;
  const [loading, setLoading] = useState(true);
  const [isConsentOutdated, setIsConsentOutdated] = useState(false);
  const [hipaaBodyText, setHipaaBodyText] = useState('');
  const {
    showApiErrorModal,
    showNetworkErrorModal
  } = useApiError();
  const {
    handleSignInSuccessAppMessage
  } = useAuth();
  const handleFetchingError = useCallback(() => {
    if (isAccountProfile) {
      showNetworkErrorModal();
      mediator.publish('router:back');
      return;
    }
    setHipaaBodyText('Hipaa.content.hipaaRichText.bodyText');
  }, [isAccountProfile, showNetworkErrorModal]);
  const fetchHipaaConsentText = useCallback(async () => {
    try {
      var _data$Hipaa;
      const hipaaCmsPath = getDocumentPathFromLanguage(hipaaCmsPaths, phoneLanguage);
      const hipaaCmsUrl = `${cmsDomain}${hipaaCmsPath}`;
      const response = await axios.get(hipaaCmsUrl);
      const data = response.data;
      if (!(data !== null && data !== void 0 && (_data$Hipaa = data.Hipaa) !== null && _data$Hipaa !== void 0 && (_data$Hipaa = _data$Hipaa.content) !== null && _data$Hipaa !== void 0 && (_data$Hipaa = _data$Hipaa.hipaaRichText) !== null && _data$Hipaa !== void 0 && _data$Hipaa.bodyText)) {
        handleFetchingError();
        setLoading(false);
        return;
      }
      setHipaaBodyText(data.Hipaa.content.hipaaRichText.bodyText);
      if (isAccountProfile && hasPendingConsent(auth.include.patient.consents.hipaa)) {
        store.dispatch({
          type: SHOW_MODAL,
          modalProps: {
            details: i18n.t('Consents.modals.reviewConsent.body'),
            testID: 'ReviewConsent'
          }
        });
        setIsConsentOutdated(true);
      }
    } catch {
      handleFetchingError();
    } finally {
      setLoading(false);
    }
  }, [auth.include.patient.consents.hipaa, cmsDomain, handleFetchingError, hipaaCmsPaths, isAccountProfile, phoneLanguage]);
  useEffect(() => {
    fetchHipaaConsentText();
  }, [fetchHipaaConsentText]);
  const handleApiRequestError = useCallback(error => {
    switch (error.status) {
      case ApiStatus.UNAUTHORIZED:
        logout();
        break;
      default:
        error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
    }
  }, [showApiErrorModal, showNetworkErrorModal]);
  const handleConsentWrapper = useCallback(async action => {
    return new Promise((resolve, reject) => {
      handleConsent(auth.account_id, action, osType, osVersion, app, appVersion, 'hipaa').then(resp => {
        const updatedAuthSession = {
          ...auth,
          include: {
            patient: {
              ...auth.include.patient,
              consents: {
                ...auth.include.patient.consents,
                hipaa: {
                  ...resp
                }
              }
            }
          }
        };
        store.dispatch({
          type: AUTH_SESSION,
          authSession: updatedAuthSession
        });
        notifyApp('adc-webview:consents', {
          id: 'hipaa',
          action,
          location: isAccountProfile ? 'profile' : 'registration'
        });
        resolve(updatedAuthSession);
      }).catch(error => {
        handleApiRequestError(error);
        reject(error);
      });
    });
  }, [app, appVersion, auth, handleApiRequestError, isAccountProfile, osType, osVersion]);
  const handleSubmitHipaaAction = useCallback(async action => {
    try {
      var _auth$include$patient;
      setLoading(true);
      const updatedAuthSession = await handleConsentWrapper(action);
      if (!((_auth$include$patient = auth.include.patient.consents) !== null && _auth$include$patient !== void 0 && (_auth$include$patient = _auth$include$patient.realWorldEvidence) !== null && _auth$include$patient !== void 0 && _auth$include$patient.at)) {
        mediator.publish('router:navigate', '/rwe');
        return;
      }
      handleSignInSuccessAppMessage(updatedAuthSession);
      setIsConsentOutdated(false);
      if (isAccountProfile) {
        // Ends the loading state if the user is on the account profile page
        setLoading(false);
        return;
      }

      // This timeout is added to give the app time to redirect the user back.
      setTimeout(() => {
        setLoading(false);
      }, APP_RETURN_TIMEOUT);
    } catch (err) {
      setLoading(false);
    }
  }, [(_auth$include$patient2 = auth.include.patient.consents) === null || _auth$include$patient2 === void 0 || (_auth$include$patient2 = _auth$include$patient2.realWorldEvidence) === null || _auth$include$patient2 === void 0 ? void 0 : _auth$include$patient2.at, handleConsentWrapper, handleSignInSuccessAppMessage, isAccountProfile]);
  const onClosePressed = useCallback(() => {
    mediator.publish('router:back');
  }, []);
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 220,
      columnNumber: 5
    }
  }, loading && /*#__PURE__*/React.createElement(LoadingModal, {
    "aria-label": "Loading",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 221,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 222,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    topNavigationProps: {
      animatedTitle: true,
      title: i18n.t('Hipaa.title'),
      buttonStart: isAccountProfile && !isConsentOutdated ? {
        element: /*#__PURE__*/React.createElement(IconBack, {
          testID: 'Hipaa.close-Pressable',
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 230,
            columnNumber: 30
          }
        }),
        label: i18n.t('Hipaa.close'),
        onPress: onClosePressed
      } : undefined
    },
    testID: "Hipaa",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 223,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 238,
      columnNumber: 11
    }
  }, hipaaBodyText.length > 0 && /*#__PURE__*/React.createElement(RichText, {
    testID: "Hipaa.content.hipaaRichText.bodyText",
    text: hipaaBodyText,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 240,
      columnNumber: 15
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 243,
      columnNumber: 11
    }
  }, isConsentOutdated || !isAccountProfile ? /*#__PURE__*/React.createElement(Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 245,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 246,
      columnNumber: 17
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: () => handleSubmitHipaaAction('accept'),
    testID: "Hipaa.accept",
    "aria-label": i18n.t('Global.microcopy.common.accept'),
    label: i18n.t('Global.microcopy.common.accept'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 247,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: () => handleSubmitHipaaAction('decline'),
    label: i18n.t('Global.microcopy.common.decline'),
    "aria-label": i18n.t('Global.microcopy.common.decline'),
    testID: "Hipaa.decline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 253,
      columnNumber: 19
    }
  }))) : /*#__PURE__*/React.createElement(OptInOut, {
    optionStrings: {
      titleTag: 'Global.microcopy.common.status',
      positiveTag: 'Global.microcopy.common.accepted',
      negativeTag: 'Global.microcopy.common.declined',
      positiveOption: 'Global.microcopy.common.accept',
      negativeOption: 'Global.microcopy.common.decline'
    },
    initialState: !!((_auth$include$patient3 = auth.include.patient) !== null && _auth$include$patient3 !== void 0 && (_auth$include$patient3 = _auth$include$patient3.consents) !== null && _auth$include$patient3 !== void 0 && (_auth$include$patient3 = _auth$include$patient3.hipaa) !== null && _auth$include$patient3 !== void 0 && _auth$include$patient3.accepted) ? 'accept' : 'decline',
    handleConsent: handleConsentWrapper,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 263,
      columnNumber: 15
    }
  })))));
};
export default connector(HipaaConsent);